import moment from 'moment'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { usePSUser } from '../../hooks'
import { useScrollIntoView } from '../../hooks/useScrollIntoView'
import { useWindowResize } from '../../hooks/useWindowResize'
import {
    Bookable,
    BookablesByDate,
    groupOverlappingSortedBookables,
    isMultiDay,
    sortBookablesByStartTime,
} from '../../models/Bookable'
import { Payee } from '../../models/Payee'
import { User } from '../../models/User'
import {
    ALL_DAY_HEADER_HEIGHT,
    BOOKABLE_GAP,
    BOOKABLE_PADDING_OFFSET,
    CalendarView,
    MIN_BOOKABLE_WIDTH,
    OVERFLOW_BUTTON_OFFSET,
    TIME_COLUMN_OFFSET,
    WEEK_HEADER_HEIGHT,
    getCalendar4DaysByDate,
    getCalendarWeekByDate,
    getUserStartHours,
} from '../../utils/calendar'
import { DAILY_HOURS, DATE_KEY_FORMAT, SHORT_DOW_FORMAT, isToday } from '../../utils/dates'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { CalendarCurrentTimeLine } from './CalendarCurrentTimeLine'
import { CalendarDayBookables } from './CalendarDayBookables'

type Props = {
    heightOffset: number
    currentView: CalendarView
    currentDate: moment.Moment
    pros: User[]
    bookablesByDate: BookablesByDate
    onGoToDay: (day: moment.Moment) => void
}

export const CalendarWeek = ({
    heightOffset,
    currentView,
    currentDate,
    pros,
    bookablesByDate,
    onGoToDay,
}: Props) => {
    const user = usePSUser()
    const timeRefs = useRef<{ [key: number]: HTMLDivElement }>({})
    const timeLineRef = useRef<null | HTMLDivElement>(null)
    const calendarBodyRef = useRef<HTMLDivElement>(null)

    const payees = useMemo(() => pros.map((u) => u.subtype) as Payee[], [pros])
    const weekDays = useMemo(
        () =>
            currentView === '4day'
                ? getCalendar4DaysByDate(currentDate)
                : getCalendarWeekByDate(currentDate),
        [currentView, currentDate]
    )

    const weekContainsToday = useMemo(
        () => weekDays.filter((d) => isToday(d)).length > 0,
        [weekDays]
    )

    const startHour = useMemo(() => (user ? Math.min(...getUserStartHours(user)) : 0), [user])

    useScrollIntoView(timeLineRef.current, { behavior: 'smooth', block: 'center' })
    useScrollIntoView(timeRefs.current[startHour - 1], {
        condition: !timeLineRef.current && !!startHour && startHour - 1 in timeRefs.current,
    })

    const { width } = useWindowResize()
    const [maxBookablesPerGroup, setMaxBookablesPerGroup] = useState(2)

    useLayoutEffect(() => {
        if (!calendarBodyRef.current) return
        const PADDING_OFFSET = BOOKABLE_PADDING_OFFSET + OVERFLOW_BUTTON_OFFSET
        const MIN_WIDTH = MIN_BOOKABLE_WIDTH + BOOKABLE_GAP
        const availableWidth =
            (calendarBodyRef.current.clientWidth - TIME_COLUMN_OFFSET) / weekDays.length -
            PADDING_OFFSET
        const maxBookables = Math.floor(availableWidth / MIN_WIDTH)
        setMaxBookablesPerGroup(maxBookables > 0 ? maxBookables : 1)
    }, [width, weekDays.length])

    // TODO: handle multi day bookables and stretch them across days
    const getAllDayBookables = useCallback(
        (day: moment.Moment): Bookable[] =>
            (bookablesByDate[day.format(DATE_KEY_FORMAT)] ?? []).filter((b: Bookable) =>
                isMultiDay(b)
            ),
        [bookablesByDate]
    )

    const hasAllDayBookables = useMemo(
        () => weekDays.some((day) => getAllDayBookables(day).length > 0),
        [weekDays, getAllDayBookables]
    )

    const getBookables = useCallback(
        (day: moment.Moment) =>
            groupOverlappingSortedBookables(
                day,
                (bookablesByDate[day.format(DATE_KEY_FORMAT)] ?? [])
                    .filter((b: Bookable) => !isMultiDay(b))
                    .sort(sortBookablesByStartTime)
            ),
        [bookablesByDate]
    )

    return (
        <Card
            className="mt-6 mb-8"
            style={{
                height: `calc(100% - ${heightOffset}px)`,
                maxWidth: '100%',
                minWidth: '1024px',
            }}
        >
            <div
                className="flex items-center rounded-t-lg border-[1px] border-gray-300 bg-gray-100"
                style={{ height: `${WEEK_HEADER_HEIGHT}px` }}
            >
                <div className="flex h-full min-w-[98px] flex-col items-center justify-center">
                    <Paragraph size="xs" className="text-gray-600">
                        {moment().format('z')}
                    </Paragraph>
                </div>
                {weekDays.map((day) => {
                    return (
                        <div
                            key={day.toISOString()}
                            className="mx-auto my-0 flex h-full min-w-[114px] flex-1 flex-col items-center justify-center nth-2:border-l-[1px] nth-2:border-l-gray-300"
                        >
                            <Paragraph size="sm" className="text-gray-600">
                                {day.format(SHORT_DOW_FORMAT)}
                            </Paragraph>
                            {!isToday(day) && (
                                <Button
                                    shape="circle"
                                    size="small"
                                    intent="tertiary_deprecated"
                                    className="no-underline"
                                    onClick={() => onGoToDay(day)}
                                >
                                    <Heading size="xs" className="text-gray-700">
                                        {day.date()}
                                    </Heading>
                                </Button>
                            )}
                            {isToday(day) && (
                                <Button
                                    shape="circle"
                                    size="small"
                                    intent="primary"
                                    onClick={() => onGoToDay(day)}
                                >
                                    <Heading size="xs">{day.date()}</Heading>
                                </Button>
                            )}
                        </div>
                    )
                })}
            </div>
            {hasAllDayBookables && (
                <div
                    className="flex border-[1px] border-t-0 border-gray-300 bg-gray-100"
                    style={{ height: `${ALL_DAY_HEADER_HEIGHT}px` }}
                >
                    <div className="flex h-full min-w-[98px] flex-col items-center justify-center">
                        <Label size="xs" className="text-gray-700">
                            All day
                        </Label>
                    </div>
                    {weekDays.map((day) => (
                        <div
                            key={day.toISOString()}
                            className="flex min-w-[114px] flex-1 flex-col border-l-[1px] border-l-gray-300 p-1"
                        >
                            <CalendarDayBookables
                                day={day}
                                payees={payees}
                                bookables={getAllDayBookables(day)}
                                threshold={getAllDayBookables(day).length === 3 ? 3 : 2}
                            />
                        </div>
                    ))}
                </div>
            )}
            <div
                ref={calendarBodyRef}
                className="scrollbar-hide relative flex overflow-x-clip overflow-y-auto overscroll-contain rounded-b-lg border-[1px] border-t-0 border-gray-300"
                style={{
                    height: `calc(100% - ${hasAllDayBookables ? WEEK_HEADER_HEIGHT + ALL_DAY_HEADER_HEIGHT : WEEK_HEADER_HEIGHT}px)`,
                }}
            >
                <div className="flex h-fit min-w-[98px] flex-col items-center justify-start gap-[48px] px-0 py-[57px]">
                    {DAILY_HOURS.slice(1).map((hour, index) => (
                        <div
                            key={hour}
                            ref={(el) => {
                                if (el) timeRefs.current[index + 1] = el
                            }}
                            className="w-full"
                        >
                            <div className="flex items-center justify-between">
                                <Paragraph size="xs" className="mx-auto text-gray-600">
                                    {hour}
                                </Paragraph>
                                <div className="h-px w-4 border-b-[1px] border-b-gray-300 pt-px"></div>
                            </div>
                        </div>
                    ))}
                </div>
                {weekDays.map((day) => (
                    <div
                        key={day.toISOString()}
                        className="relative flex h-fit min-w-[114px] flex-1 flex-col items-center justify-center gap-[67px] border-l-[1px] border-l-gray-300 px-0 py-[67px]"
                    >
                        <div className="absolute top-0 left-0 size-full p-1">
                            {getBookables(day).map((bookablesGroup) => (
                                <CalendarDayBookables
                                    key={bookablesGroup.start}
                                    day={day}
                                    payees={payees}
                                    bookablesGroup={bookablesGroup}
                                    threshold={maxBookablesPerGroup}
                                />
                            ))}
                        </div>

                        {DAILY_HOURS.slice(1).map((hour) => (
                            <div
                                key={hour}
                                className="self-stretch border-t-[1px] border-t-gray-300"
                            ></div>
                        ))}
                    </div>
                ))}
                {weekContainsToday && <CalendarCurrentTimeLine ref={timeLineRef} />}
            </div>
        </Card>
    )
}
